import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Plus, Settings, Sparkles, X } from "lucide-react";
import { Badge, Button, Combobox, Input, Label, Textarea } from "@/components/ui";
import { Blog } from "@/services/bedrock";
import { cn } from "@/lib/utils";
import { emptyBlogSection, moveItem, normalizeBlogBody } from "../blogBody";
import { BlogSectionBlocks } from "./BlogSectionBlocks";
import { beginPointerReorder, ReorderGhost, ReorderGhostOverlay } from "./pointerReorder";

export type ThemeOption = { id: number; label: string };

type Props = {
  draft: EditableBlog;
  readOnly: boolean;
  themeOptions: ThemeOption[];
  canChangeTheme: boolean;
  themeSettingsUrl?: string;
  onChange: (patch: Partial<EditableBlog>) => void;
  onSectionHover?: (index: number | null) => void;
  onSectionExpand?: (index: number, blockIndex?: number) => void;
  onSectionInputBlur?: () => void;
  onSectionDrag?: (index: number | null) => void;
  onPartFocus?: (part: "title" | "subtitle" | "tags" | "takeaways" | null) => void;
};

export type EditableBlog = {
  title: string;
  subtitle: string;
  metaDescription: string;
  excerpt: string;
  tags: string[];
  themeId: number | null;
  body: Blog.Body;
};

export { emptyBlogSection } from "../blogBody";

export const parseTags = (value: string): string[] =>
  value
    .split(",")
    .map(tag => tag.trim())
    .filter(Boolean);

export const toEditableBlog = (blog: Blog): EditableBlog => ({
  title: blog.title ?? "",
  subtitle: blog.subtitle ?? "",
  metaDescription: blog.metaDescription ?? "",
  excerpt: blog.excerpt ?? "",
  tags: blog.tags ?? [],
  themeId: blog.contentPageThemeId ?? null,
  body: normalizeBlogBody(blog.body),
});

/**
 * Decides what the editor should show when a fresh copy of the blog arrives.
 *
 * A blog is created empty and filled in asynchronously, so the editor is seeded twice: once from
 * the placeholder record while the article is being written, then again from the finished article.
 * Once the Player can actually type, their in-progress edits win over later refreshes.
 */
export const nextEditorDraft = (
  next: Blog,
  current: EditableBlog | undefined,
  seededFrom: Blog | undefined,
  reseedEditor = false
): EditableBlog => {
  const replacingPlaceholder = seededFrom?.status === "GENERATING";
  const keepCurrent = !!current && !replacingPlaceholder && !reseedEditor;
  return keepCurrent ? current! : toEditableBlog(next);
};

const TagChipsInput: React.FC<{
  tags: string[];
  disabled?: boolean;
  onChange: (tags: string[]) => void;
  onFocus?: () => void;
  onBlur?: () => void;
}> = ({ tags, disabled, onChange, onFocus, onBlur }) => {
  const [value, setValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const addTags = (raw: string) => {
    const incoming = parseTags(raw);
    if (incoming.length === 0) return;

    const existing = new Set(tags.map(tag => tag.toLowerCase()));
    const next = [...tags];
    incoming.forEach(tag => {
      if (existing.has(tag.toLowerCase())) return;
      existing.add(tag.toLowerCase());
      next.push(tag);
    });
    onChange(next);
    setValue("");
  };

  const removeTag = (index: number) => onChange(tags.filter((_, i) => i !== index));

  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-2 min-h-9 w-full rounded-lg border border-input bg-background px-2.5 py-1.5",
        "focus-within:border-ring focus-within:ring-ring/50 focus-within:ring-3",
        disabled && "opacity-50 pointer-events-none"
      )}
      onClick={() => inputRef.current?.focus()}
    >
      {tags.map((tag, index) => (
        <Badge key={`${tag}-${index}`} variant="outline" className="text-sm px-2 py-0.5 inline-flex items-center gap-1">
          {tag}
          {!disabled && (
            <X
              size={13}
              className="cursor-pointer hover:text-foreground"
              onClick={event => {
                event.stopPropagation();
                removeTag(index);
              }}
            />
          )}
        </Badge>
      ))}
      <input
        ref={inputRef}
        id="blog-tags"
        value={value}
        disabled={disabled}
        placeholder={tags.length === 0 ? "Type a tag and press Enter" : undefined}
        className="flex-1 min-w-24 bg-transparent border-none outline-none text-sm placeholder:text-muted-foreground"
        onChange={event => setValue(event.target.value)}
        onFocus={onFocus}
        onBlur={() => {
          if (value.trim()) addTags(value);
          onBlur?.();
        }}
        onKeyDown={event => {
          if (event.key === "Enter" || event.key === "," || event.key === "Tab") {
            if (!value.trim()) return;
            event.preventDefault();
            addTags(value.replace(/,$/, ""));
          } else if (event.key === "Backspace" && !value && tags.length > 0) {
            removeTag(tags.length - 1);
          }
        }}
      />
    </div>
  );
};

export const BlogEditor: React.FC<Props> = ({
  draft,
  readOnly,
  themeOptions,
  canChangeTheme,
  themeSettingsUrl,
  onChange,
  onSectionHover,
  onSectionExpand,
  onSectionInputBlur,
  onSectionDrag,
  onPartFocus,
}) => {
  const sections = draft.body.sections ?? [];
  const keyTakeaways = draft.body.keyTakeaways ?? [];
  const hasTakeaways = keyTakeaways.some(item => item.trim());
  const [takeawaysOpen, setTakeawaysOpen] = useState(hasTakeaways);
  const [sectionKeys, setSectionKeys] = useState<string[]>(() => sections.map(() => crypto.randomUUID()));
  const [openedSectionKeys, setOpenedSectionKeys] = useState<string[]>([]);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragGhost, setDragGhost] = useState<ReorderGhost | null>(null);
  const sectionsRef = useRef(sections);
  const sectionKeysRef = useRef(sectionKeys);
  sectionsRef.current = sections;
  sectionKeysRef.current = sectionKeys;

  useEffect(() => {
    setSectionKeys(current => {
      if (current.length === sections.length) return current;
      if (current.length < sections.length) {
        return [...current, ...Array.from({ length: sections.length - current.length }, () => crypto.randomUUID())];
      }
      return current.slice(0, sections.length);
    });
  }, [sections.length]);

  const updateSections = (next: Blog.Section[], nextKeys?: string[]) => {
    if (nextKeys) setSectionKeys(nextKeys);
    onChange({ body: { ...draft.body, sections: next } });
  };

  const updateSection = (index: number, next: Blog.Section) => {
    updateSections(sections.map((section, i) => (i === index ? next : section)));
  };

  const addSectionAt = (index: number) => {
    const key = crypto.randomUUID();
    const next = [...sections];
    next.splice(index, 0, emptyBlogSection());
    const nextKeys = [...sectionKeys];
    nextKeys.splice(index, 0, key);
    setOpenedSectionKeys(current => [...current, key]);
    updateSections(next, nextKeys);
  };

  const addSection = () => addSectionAt(sections.length);

  const removeSection = (index: number) => {
    updateSections(
      sections.filter((_, i) => i !== index),
      sectionKeys.filter((_, i) => i !== index)
    );
  };

  const startDrag = (event: React.PointerEvent, index: number, card: HTMLElement) => {
    beginPointerReorder({
      event,
      card,
      index,
      itemSelector: "[data-blog-section]",
      onReorder: (from, to) => {
        const next = moveItem(sectionsRef.current, from, to);
        const nextKeys = moveItem(sectionKeysRef.current, from, to);
        sectionsRef.current = next;
        sectionKeysRef.current = nextKeys;
        updateSections(next, nextKeys);
      },
      onActiveChange: index => {
        setDragIndex(index);
        onSectionDrag?.(index);
      },
      onGhostChange: setDragGhost,
    });
  };

  // Keep the current theme selectable even when it is not in the fetched list, e.g. a Coach theme.
  const themeItems = useMemo(() => {
    const merged = [...themeOptions];
    if (draft.themeId != null && !merged.some(option => option.id === draft.themeId)) {
      merged.unshift({ id: draft.themeId, label: "Theme" });
    }
    return merged;
  }, [themeOptions, draft.themeId]);

  const selectedTheme = useMemo(
    () => (draft.themeId == null ? null : themeItems.find(option => option.id === draft.themeId) ?? null),
    [draft.themeId, themeItems]
  );
  const [listingOpen, setListingOpen] = useState(!draft.excerpt && !draft.metaDescription);

  useEffect(() => {
    if (hasTakeaways) setTakeawaysOpen(true);
  }, [hasTakeaways]);

  return (
    <div className="flex flex-col gap-5">
      <div className="rounded-2xl border border-indigo-100 bg-gradient-to-br from-indigo-50/80 via-white to-sky-50/60 p-5 shadow-sm">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="blog-title" className="text-indigo-900/70">
              Title
            </Label>
            <Input
              id="blog-title"
              value={draft.title}
              disabled={readOnly}
              placeholder="Give this blog a title"
              className="h-11 border-indigo-100 bg-white text-lg font-semibold"
              onFocus={() => onPartFocus?.("title")}
              onBlur={() => onPartFocus?.(null)}
              onChange={e => onChange({ title: e.target.value })}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="blog-subtitle">Subtitle</Label>
            <Input
              id="blog-subtitle"
              value={draft.subtitle}
              disabled={readOnly}
              placeholder="A short hook under the title"
              onFocus={() => onPartFocus?.("subtitle")}
              onBlur={() => onPartFocus?.(null)}
              onChange={e => onChange({ subtitle: e.target.value })}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="blog-tags">Tags</Label>
            <TagChipsInput
              tags={draft.tags}
              disabled={readOnly}
              onChange={tags => onChange({ tags })}
              onFocus={() => onPartFocus?.("tags")}
              onBlur={() => onPartFocus?.(null)}
            />
          </div>

          {canChangeTheme && (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="blog-theme">Theme</Label>
              <div className="flex flex-row items-center gap-2">
                <Combobox
                  id="blog-theme"
                  className="flex-1 min-w-0 w-full"
                  placeholder="Default Theme"
                  items={themeItems}
                  value={selectedTheme}
                  showClear
                  disabled={readOnly}
                  onValueChange={option => onChange({ themeId: option?.id ?? null })}
                  itemToStringLabel={option => option.label}
                />
                {themeSettingsUrl && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-lg"
                    aria-label="Open theme settings"
                    onClick={() => window.open(themeSettingsUrl, "_blank")}
                  >
                    <Settings className="size-5" />
                  </Button>
                )}
              </div>
            </div>
          )}

          <button
            type="button"
            className="flex items-center gap-2 self-start text-xs font-semibold uppercase tracking-wide text-indigo-700 hover:text-indigo-900"
            onClick={() => setListingOpen(open => !open)}
          >
            <ChevronDown className={cn("size-4 transition-transform", listingOpen && "rotate-180")} />
            Listing details
          </button>
          {listingOpen && (
            <div className="flex flex-col gap-3 rounded-xl border border-indigo-100 bg-white/80 p-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="blog-excerpt">Excerpt</Label>
                <Textarea
                  id="blog-excerpt"
                  rows={2}
                  value={draft.excerpt}
                  disabled={readOnly}
                  placeholder="Shown on cards and listings"
                  onChange={e => onChange({ excerpt: e.target.value })}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="blog-meta">Search description</Label>
                <Textarea
                  id="blog-meta"
                  rows={2}
                  value={draft.metaDescription}
                  disabled={readOnly}
                  placeholder="About 160 characters"
                  onChange={e => onChange({ metaDescription: e.target.value })}
                />
                <span className="text-xs text-muted-foreground">{draft.metaDescription.length}/160</span>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <div className="text-sm font-bold uppercase tracking-wider text-slate-500">Article</div>
        {sections.length === 0 && (
          <div className="rounded-2xl border border-dashed border-indigo-200 bg-indigo-50/40 px-4 py-8 text-center text-sm text-indigo-800/70">
            Add a section to start shaping the article.
          </div>
        )}
        {sections.map((section, index) => (
          <BlogSectionBlocks
            key={sectionKeys[index] ?? `section-${index}`}
            section={section}
            sectionIndex={index}
            defaultOpen={openedSectionKeys.includes(sectionKeys[index])}
            readOnly={readOnly}
            dragging={dragIndex === index}
            onChange={next => updateSection(index, next)}
            onRemove={() => removeSection(index)}
            onHover={index => {
              if (dragIndex != null) return;
              onSectionHover?.(index);
            }}
            onExpand={onSectionExpand}
            onInputBlur={onSectionInputBlur}
            onMoveStart={startDrag}
          />
        ))}
        <Button
          type="button"
          variant="outline"
          disabled={readOnly}
          onClick={addSection}
          className="h-12 w-full border-dashed border-indigo-200 bg-indigo-50/60 text-indigo-800 hover:bg-indigo-100 hover:text-indigo-950"
        >
          <Plus className="size-4" />
          Add section
        </Button>
      </div>

      {takeawaysOpen ? (
        <div className="flex flex-col gap-2 rounded-2xl border border-amber-200 bg-amber-50/70 p-4">
          <Label htmlFor="blog-takeaways" className="inline-flex items-center gap-2 text-amber-900">
            <Sparkles className="size-4" />
            Key takeaways
          </Label>
          <Textarea
            id="blog-takeaways"
            rows={3}
            value={keyTakeaways.join("\n")}
            disabled={readOnly}
            placeholder="One takeaway per line"
            className="bg-white"
            onFocus={() => onPartFocus?.("takeaways")}
            onBlur={() => onPartFocus?.(null)}
            onChange={e =>
              onChange({
                body: { ...draft.body, keyTakeaways: e.target.value.split("\n").map(item => item.trim()) },
              })
            }
          />
        </div>
      ) : (
        !readOnly && (
          <Button
            type="button"
            variant="outline"
            onClick={() => setTakeawaysOpen(true)}
            className="h-12 w-full border-dashed border-amber-200 bg-amber-50/60 text-amber-900 hover:bg-amber-100 hover:text-amber-950"
          >
            <Sparkles className="size-4" />
            Add key takeaways
          </Button>
        )
      )}
      <ReorderGhostOverlay ghost={dragGhost} />
    </div>
  );
};
